import { defineStore } from 'pinia';
import { ref } from 'vue';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
	label: string;
	handler: () => void;
}

export interface ToastOptions {
	title?: string;
	message?: string;
	icon?: string;
	/** Durée d'affichage en millisecondes. `0` rend le message persistant. */
	timeout?: number;
	action?: ToastAction;
}

export interface ToastItem extends ToastOptions {
	id: string;
	variant: ToastVariant;
}

const DEFAULT_TIMEOUT_MS = 5000;

/**
 * File des messages transitoires.
 *
 * Les minuteurs sont conservés hors de l'état réactif et annulés à la fermeture :
 * un message fermé à la main ne peut pas être refermé une seconde fois par son
 * minuteur, ce qui provoquerait la disparition du message suivant.
 */
export const useToastStore = defineStore('toast', () => {
	const toasts = ref<ToastItem[]>([]);
	const timers = new Map<string, ReturnType<typeof setTimeout>>();

	function dismiss(id: string): void {
		const timer = timers.get(id);
		if (timer !== undefined) {
			clearTimeout(timer);
			timers.delete(id);
		}
		toasts.value = toasts.value.filter((toast) => toast.id !== id);
	}

	function push(variant: ToastVariant, options: ToastOptions): string {
		const id = globalThis.crypto?.randomUUID?.() ?? `toast-${Date.now()}-${Math.random()}`;
		toasts.value.unshift({ ...options, id, variant });

		const timeout = options.timeout ?? DEFAULT_TIMEOUT_MS;
		if (timeout > 0) {
			timers.set(
				id,
				setTimeout(() => {
					timers.delete(id);
					dismiss(id);
				}, timeout),
			);
		}
		return id;
	}

	const success = (options: ToastOptions = {}) =>
		push('success', { message: 'Opération effectuée.', ...options });

	const error = (options: ToastOptions = {}) =>
		push('error', { message: 'Une erreur est survenue.', timeout: 8000, ...options });

	const warning = (options: ToastOptions = {}) => push('warning', options);
	const info = (options: ToastOptions = {}) => push('info', options);

	return { toasts, dismiss, success, error, warning, info };
});
